import React from 'react';
import styles from '../capitalExpenditure.module.scss';
import InputField from './InputField';
import { formatCalculationResult, parseNumericValue } from '../utils/numberUtils';

const blankIfZero = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(num) && num === 0 ? '' : String(value);
};

export const INCOME_TOTAL_FIELD_MAP = [
  ['incomeUnallocatedBudget', 'income_unallocated_budget'],
  ['incomeRevisedBudget', 'income_revised_budget'],
  ['incomeQ1BudgetDemand', 'income_q1_budget_demand'],
  ['incomeQ1BudgetExpenditure', 'income_q1_budget_expenditure'],
  ['incomeQ1AuthorityReceived', 'income_q1_authority_received'],
  ['incomeQ1Expense', 'income_q1_expense'],
  ['incomeQ1RemainingBalance', 'income_q1_remaining_balance'],
  ['incomeQ2BudgetDemand', 'income_q2_budget_demand'],
  ['incomeQ2BudgetExpenditure', 'income_q2_budget_expenditure'],
  ['incomeQ2AuthorityReceived', 'income_q2_authority_received'],
  ['incomeQ2Expense', 'income_q2_expense'],
  ['incomeQ2RemainingBalance', 'income_q2_remaining_balance'],
  ['incomeQ3BudgetDemand', 'income_q3_budget_demand'],
  ['incomeQ3BudgetExpenditure', 'income_q3_budget_expenditure'],
  ['incomeQ3AuthorityReceived', 'income_q3_authority_received'],
  ['incomeQ3Expense', 'income_q3_expense'],
  ['incomeQ3RemainingBalance', 'income_q3_remaining_balance'],
  ['incomeQ4BudgetDemand', 'income_q4_budget_demand'],
  ['incomeQ4BudgetExpenditure', 'income_q4_budget_expenditure'],
  ['incomeQ4AuthorityReceived', 'income_q4_authority_received'],
  ['incomeQ4Expense', 'income_q4_expense'],
  ['incomeQ4RemainingBalance', 'income_q4_remaining_balance'],
  ['incomeBudgetDemand', 'income_budget_demand'],
  ['incomeBudgetExpenditure', 'income_budget_expenditure'],
  ['incomeAuthorityReceived', 'income_authority_received'],
  ['incomeExpense', 'income_expense'],
  ['incomeRemainingBalance', 'income_remaining_balance'],
];

export const emptyIncomeTotals = () => (
  Object.fromEntries(INCOME_TOTAL_FIELD_MAP.map(([camel]) => [camel, '']))
);

export const incomePayloadFromState = (totals = {}) => (
  Object.fromEntries(
    INCOME_TOTAL_FIELD_MAP.map(([camel, snake]) => [snake, parseNumericValue(totals[camel])])
  )
);

export const incomeStateFromApi = (data = {}) => (
  Object.fromEntries(
    INCOME_TOTAL_FIELD_MAP.map(([camel, snake]) => [camel, blankIfZero(data[snake])])
  )
);

const QUARTER_CELLS = [1, 2, 3, 4].flatMap((q) => ([
  [`incomeQ${q}BudgetDemand`, 'बजेट माग'],
  [`incomeQ${q}BudgetExpenditure`, 'बजेट निकासा'],
  [`incomeQ${q}AuthorityReceived`, 'अख्तियारी प्राप्त'],
  [`incomeQ${q}Expense`, 'खर्च'],
  [`incomeQ${q}RemainingBalance`, 'बाँकि मौज्दात'],
]));

const FY_CELLS = [
  ['incomeBudgetDemand', 'कुल बजेट माग'],
  ['incomeBudgetExpenditure', 'कुल बजेट निकासा'],
  ['incomeAuthorityReceived', 'कुल अख्तियारी'],
  ['incomeExpense', 'कुल खर्च'],
  ['incomeRemainingBalance', 'जम्मा मौज्दात'],
];

/**
 * जम्मा आम्दानी — after जम्मा संचालन र पुँजीगत खर्च.
 * Merge only first two columns (कोड + शिर्षक). All numeric columns are
 * manual like यस आ.व.को कुल बजेट विवरण.
 */
const IncomeTotalRow = ({
  incomeTotals = {},
  onIncomeChange,
  viewMode = false
}) => {
  const set = (field) => (rawOrEvent, englishValue) => {
    if (!onIncomeChange || viewMode) return;
    const raw = typeof rawOrEvent === 'string' || typeof rawOrEvent === 'number'
      ? rawOrEvent
      : rawOrEvent?.target?.value;
    const next = englishValue !== undefined && englishValue !== null && englishValue !== ''
      ? englishValue
      : (raw ?? '');
    onIncomeChange(field, String(next).replace(/,/g, ''));
  };

  const renderCell = (field, placeholder, extraClass = '') => (
    <td key={field} className={`${styles.budgetCell} ${styles.incomeCell} ${extraClass}`.trim()}>
      <InputField
        value={incomeTotals[field] ?? ''}
        onChange={set(field)}
        placeholder={placeholder}
        readOnly={viewMode}
        viewMode={viewMode}
        isNumeric
      />
    </td>
  );

  const difference = parseNumericValue(incomeTotals.incomeRevisedBudget)
    - parseNumericValue(incomeTotals.incomeBudgetDemand);

  return (
    <tr className={`${styles.totalRow} ${styles.incomeRow}`}>
      <td colSpan="2" className={`${styles.totalLabel} ${styles.stickyColGroup2} ${styles.incomeLabel}`}>
        जम्मा आम्दानी
      </td>
      {renderCell('incomeUnallocatedBudget', 'बिनियोजित बजेट', styles.stickyCol3)}
      {renderCell('incomeRevisedBudget', 'परिमार्जीत बजेट', styles.stickyCol4)}
      {QUARTER_CELLS.map(([field, placeholder]) => renderCell(field, placeholder))}
      {FY_CELLS.map(([field, placeholder]) => renderCell(field, placeholder))}
      <td className={`${styles.budgetCell} ${styles.incomeCell}`}>
        {formatCalculationResult(difference)}
      </td>
    </tr>
  );
};

export default IncomeTotalRow;
